import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ITableColumn } from "../../../../components";
import DataTable from "../../../../components/components/Table/DataTable";
import PolicyTransactionPremiumForm from "./PolicyTransactionPremiumForm";
import PolicyTransactionPremiumService from "./PolicyTransactionPremiumService";
import { PolicyTransactionPremiumDto } from "../../../model/PolicyTransactionPremiumDto";
import { RootState } from "../../../../shared/store";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { uuid } from "../../../../shared/util/uuid";
import { CommonUtility } from "../../../../Services/CommonUtility";

export interface IPolicyTransactionPremiumGridProps {
  policyId?: string;
  polEventId?: string;
  tabIndex?: number;
}

const PolicyTransactionPremiumGrid: React.FunctionComponent<IPolicyTransactionPremiumGridProps> = (props) => {
  const { policyId, polEventId } = props;
  let tbIndex = props.tabIndex;
  const dispatch = useDispatch();
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [isFormSaved, setIsFormSaved] = useState(true);
  const [currentPremiumData, setCurrentPremiumData] = useState<PolicyTransactionPremiumDto | null>(null);
  const customerDetail = useSelector((state: RootState) => state.customerSlice);
  const policyDetailKey = CommonUtility.getPolicyDetailKey(policyId, polEventId);
  const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
  const [policyTransactionPremiums, setPolicyTransactionPremiums] = useState<PolicyTransactionPremiumDto[]>([]);
  const policyTransactionPremiumService = new PolicyTransactionPremiumService();

  useEffect(() => {
    if (policyDetails && policyDetails.policyTransactionPremiums) {
      setPolicyTransactionPremiums(policyDetails.policyTransactionPremiums);
    }
  }, [policyId, polEventId, policyDetails]);

  const onRowClick = (rowData: PolicyTransactionPremiumDto) => {
    if (expandedRowId === rowData.id) {
      setExpandedRowId(null);
      setCurrentPremiumData(null);
    } else {
      setExpandedRowId(rowData.id);
      setCurrentPremiumData(rowData);
    }
  };

  const onAddNewPremiumClick = () => {
    const newPremium: PolicyTransactionPremiumDto = {
      id: uuid(), // Generate a unique ID for the new premium
      polId: policyId, // Associate the premium with the policy ID
      polEventId: polEventId, // Explicitly add polEventId
      premium: 0, // Default value for premium
      billedPremium: null, // Default value for billed premium
      writtenPremium: 0, // Default value for written premium
      fullTermPremium: 0, // Default value for full-term premium
      howBilled: "", // Default value for how billed
      effDate: policyDetails?.polEffDate || undefined, // Default to policy effective date if available
      planType: "", // Default value for plan type
      descriptionPolTP: "", // Default value for description
    };

    setPolicyTransactionPremiums([newPremium, ...policyTransactionPremiums]);
    setCurrentPremiumData(newPremium);
    setExpandedRowId(newPremium.id); // Expand the newly added row
  };

  const onRowDeleteClick = (rowData: PolicyTransactionPremiumDto) => {
    const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));

    if (updatedPolicyDetails && updatedPolicyDetails.policyTransactionPremiums) {
      updatedPolicyDetails.policyTransactionPremiums = updatedPolicyDetails.policyTransactionPremiums.filter(
        (premium) => premium.id !== rowData.id
      );

      dispatch(
        updateSelectedPolicyDetails({
          policyId: policyId,
          polEventId: polEventId,
          policyDetails: updatedPolicyDetails,
        })
      );

      setPolicyTransactionPremiums((prevPremiums) =>
        prevPremiums.filter((premium) => premium.id !== rowData.id)
      );
    }
  };

  const renderRow = (premium: PolicyTransactionPremiumDto, index: number) => {
    return (
      <React.Fragment key={premium.id || index}>
        {expandedRowId === premium.id && (
          <tr>
            <td colSpan={tableColumns.length}>
              <PolicyTransactionPremiumForm
                transactionPremium={currentPremiumData!}
                polId={policyId}
                polEventId={polEventId}
                setIsFormSaved={setIsFormSaved}
                tabIndex={tbIndex++}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const tableColumns: ITableColumn[] = policyTransactionPremiumService.getPremiumTableColumns(onRowClick, onRowDeleteClick, tbIndex++);

  return (
    <div className="policy-transaction-premium-grid">
      <Button
        className="mb-2"
        onClick={onAddNewPremiumClick}
        disabled={policyTransactionPremiums.length >= 1} // Disable button if there is one row
        tabIndex={tbIndex++}>
        <i className="bi bi-plus-circle me-2"></i>
        Add Premium
      </Button>

      <DataTable
        columns={tableColumns}
        data={policyTransactionPremiums}
        keyColumnName="id"
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        renderRow={renderRow}
        isFormSaved={isFormSaved}
        tableClassName="table-striped table-hover table-sm"
        theadClassName="tblHeader"
      />
    </div>
  );
};

export default PolicyTransactionPremiumGrid;